import PropTypes from 'prop-types';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import useStepPost from '../../hooks/useStepPost';
import useIraApplicationFundingTypeDispatch from '../../hooks/useIraApplicationFundingTypeDispatch';
import CustodianModal from '../CustodianModal/CustodianModal';
import Paragraph from '../Paragraph/Paragraph';
import MailboxInIcon from '../Icons/MailboxInIcon';
import QrCodeIcon from '../Icons/QrCodeIcon';
import SavingBankIcon from '../Icons/SavingBankIcon';
import Back from '../Buttons/Back';
import { HeadingSubtitle } from '../Heading';
import FundingContainer from '../FundingContainer';
import { IRA_APPLICATION_FUNDING_TYPES } from '../../util/constants';
import themeColors from '../../assets/styles/themeColors';
import { getApplication } from '../../store/actions/application/applicationActions';
import useApplicationUid from '../../hooks/useApplicationUid';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';
import { retrieveFromSessionStorage } from '../../util/helpers/sessionStorageHelper';
import { ACCOUNT_TYPE_GTM } from '../../constants/sessionStorage';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';
import { ACCOUNT_STEP_VIEW } from '../../util/gtmEvents';
import { useApplicantData } from '../../hooks/useApplicantData';

const IraFundAccountSelect = ({
  nextPath,
  handleNext,
  handleBack,
  skipNextStep,
  onSkipStep,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const ApplicantData = useApplicantData();
  const { ApplicationUid } = useApplicationUid();
  const { onSuccess, onModalNextStep } = useStepPost({
    nextPath,
    handleNext,
  });
  const {
    iraApplicationFundingTypeDispatch,
  } = useIraApplicationFundingTypeDispatch();
  const [isCustodianModalOpen, setIsCustodianModalOpen] = useState(false);

  const { gtmStepEvent } = useGtmStepHook();
  const accountGtmType = retrieveFromSessionStorage(ACCOUNT_TYPE_GTM);

  const handleGtmEvent = (methodType) => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_VIEW,
      stepLabel: `Fund your account - ${methodType}`,
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountDateOfBirth: ApplicantData?.PersonBirthday,
      accountPhone: ApplicantData?.Phone,
      accountAddress: ApplicantData?.PersonAddress,
    });
  };

  const onSubmit = (value) => {
    handleGtmEvent(value);

    if (value === 'IRATransfer') {
      iraApplicationFundingTypeDispatch({
        data: {
          IRAFundingType: value,
        },
        onSuccess: () => {
          if (skipNextStep) {
            skipNextStep();
            return;
          }

          setIsCustodianModalOpen(true);
        },
      });
      return;
    }

    const getOnSuccessRoute = () => {
      if (value === IRA_APPLICATION_FUNDING_TYPES[2]) {
        return nextPath.ANNUAL_CONTRIBUTION;
      }

      if (value === IRA_APPLICATION_FUNDING_TYPES[1]) {
        return nextPath.ROLLOVER;
      }
    };

    iraApplicationFundingTypeDispatch({
      data: {
        IRAFundingType: value,
      },
      onSuccess: () => {
        if (handleNext) {
          onModalNextStep();
        } else {
          onSuccess(getOnSuccessRoute());
        }
      },
    });
  };

  const options = [
    {
      title: t('fundAccount.transferIRATitle'),
      subtitle: t('fundAccount.transferIraSubtitle'),
      description: t('fundAccount.transferIRADescription'),
      Icon: MailboxInIcon,
      onClick: () => onSubmit('IRATransfer'),
    },
    {
      title: t('fundAccount.rolloverTitle'),
      subtitle: t('fundAccount.rolloverSubtitle'),
      description: t('fundAccount.rolloverDescription'),
      Icon: QrCodeIcon,
      onClick: () => onSubmit('Rollover'),
    },
    {
      title: t('fundAccount.cashTitle'),
      subtitle: t('fundAccount.cashSubtitle'),
      description: t('fundAccount.cashDescription'),
      Icon: SavingBankIcon,
      showDivider: false,
      onClick: () => onSubmit('AnnualContribution'),
    },
  ];

  return (
    <>
      {handleBack && <Back onClick={handleBack} />}
      <HeadingSubtitle mb={12} color={themeColors.colorTextSecondary}>
        {t('fundAccount.normalTitle')}
      </HeadingSubtitle>
      <Paragraph mb={32} color={themeColors.colorTextXLLight}>
        {t('fundAccount.normalSubtitle')}
      </Paragraph>
      {options.map((option) => (
        <FundingContainer {...option} key={option.title} />
      ))}
      <CustodianModal
        isOpen={isCustodianModalOpen}
        onClose={() => {
          dispatch(getApplication({ data: { ApplicationUid } }));
          setIsCustodianModalOpen(false);
        }}
        onSuccess={onSkipStep}
      />
    </>
  );
};

IraFundAccountSelect.propTypes = {
  nextPath: PropTypes.string,
  handleNext: PropTypes.func,
  handleBack: PropTypes.func,
  skipNextStep: PropTypes.func,
  onSkipStep: PropTypes.func,
};

export default IraFundAccountSelect;
